import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Award, Loader2, Check, EyeOff, X, Send, AlertCircle, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BADGE_TYPE_LABEL = {
  competency: "Competency Badge",
  identity: "Professional Identity Badge",
  framework: "Framework Achievement",
};

const STATUS_LABEL = {
  draft: "Draft",
  approved: "Approved",
  hidden: "Hidden",
  rejected: "Rejected",
  published: "Published",
};

const STATUS_COLOUR = {
  draft: "bg-slate-200 text-slate-700",
  approved: "bg-blue-100 text-blue-800",
  hidden: "bg-amber-100 text-amber-800",
  rejected: "bg-rose-100 text-rose-800",
  published: "bg-emerald-100 text-emerald-800",
};

function BadgePill({ badge, onAction, onEditTitle, currentUserRole }) {
  // The little circular badge "icon" on the left mirrors the on-card pill
  // colour. The whole card is the "LinkedIn certifications" style the user
  // asked for in Q1 (option c): icon + descriptive pill card.
  const color = badge.color || "#16a34a";
  const initials = (badge.title || "").split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  const isAdmin = currentUserRole === "super_admin" || currentUserRole === "admin";
  const canApprove = isAdmin || currentUserRole === "assessor" || currentUserRole === "iqa";
  // Identity badges are AI-clustered titles — admins should be able to
  // override them. Framework Achievement titles are equally editable so
  // institutions can match their own awarding-body wording.
  const canEditTitle = isAdmin && (badge.badge_type === "identity" || badge.badge_type === "framework");

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-white"
      data-testid={`badge-row-${badge.badge_id}`}
    >
      <div
        className="flex items-center justify-center w-12 h-12 rounded-full text-white font-bold text-sm shadow-sm shrink-0"
        style={{ backgroundColor: color }}
        title={BADGE_TYPE_LABEL[badge.badge_type]}
      >
        {initials || "•"}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-slate-900 truncate">{badge.title}</span>
          <Badge variant="outline" className={`text-[10px] ${STATUS_COLOUR[badge.status] || "bg-slate-100 text-slate-700"}`}>
            {STATUS_LABEL[badge.status] || badge.status}
          </Badge>
          {badge.badge_type === "identity" && badge.ai_suggested_title && !badge.title_locked ? (
            <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200" title="Title was generated by AI from the achieved competencies">
              AI-clustered
            </Badge>
          ) : null}
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
          <span>{BADGE_TYPE_LABEL[badge.badge_type]}</span>
          {badge.tier ? (
            <Badge variant="outline" className={`text-[10px] ${
              badge.tier === "distinction" ? "bg-amber-100 text-amber-800 border-amber-400" :
              badge.tier === "achievement" ? "bg-yellow-100 text-yellow-800 border-yellow-400" :
              "bg-orange-100 text-orange-800 border-orange-400"
            }`} title={`Tier: ${badge.tier}`}>
              {badge.tier}
            </Badge>
          ) : null}
          {badge.subtitle ? <span>· {badge.subtitle}</span> : null}
          {badge.score != null ? <span>· Score {badge.score}/5</span> : null}
          {badge.avg_score != null && badge.badge_type === "framework" ? <span>· Avg {badge.avg_score}/5</span> : null}
          {badge.competency_code ? <span className="font-mono">· {badge.competency_code}</span> : null}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap justify-end">
        {canEditTitle ? (
          <Button size="sm" variant="ghost" onClick={() => onEditTitle(badge)} data-testid={`edit-title-${badge.badge_id}`} title="Edit badge title">
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        ) : null}
        {canApprove && badge.status === "draft" ? (
          <Button size="sm" variant="outline" onClick={() => onAction(badge, "approved")} data-testid={`approve-${badge.badge_id}`}>
            <Check className="w-3.5 h-3.5 mr-1" /> Approve
          </Button>
        ) : null}
        {isAdmin && (badge.status === "approved" || badge.status === "hidden") ? (
          <Button size="sm" onClick={() => onAction(badge, "published")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`publish-${badge.badge_id}`}>
            <Send className="w-3.5 h-3.5 mr-1" /> Publish
          </Button>
        ) : null}
        {canApprove && badge.status !== "hidden" && badge.status !== "rejected" ? (
          <Button size="sm" variant="ghost" onClick={() => onAction(badge, "hidden")} data-testid={`hide-${badge.badge_id}`}>
            <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide
          </Button>
        ) : null}
        {canApprove && badge.status !== "rejected" && badge.status !== "published" ? (
          <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onAction(badge, "rejected")} data-testid={`reject-${badge.badge_id}`}>
            <X className="w-3.5 h-3.5 mr-1" /> Reject
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function BadgesPanel({ candidateId, currentUserRole }) {
  const [data, setData] = useState({ badges: [], auto_derived: false, trigger_message: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/candidates/${candidateId}/badges`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      // 401/403 just mean the user can't see them; silent.
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  const actOnBadge = async (badge, newStatus) => {
    try {
      await axios.patch(`${API}/badges/${badge.badge_id}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Badge ${newStatus}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed.");
    }
  };

  const editBadgeTitle = async (badge) => {
    const next = window.prompt(
      `Edit ${BADGE_TYPE_LABEL[badge.badge_type] || "badge"} title:\n\n(The current title was suggested by the rule engine — your edit will be remembered and won't be overwritten by future syncs.)`,
      badge.title || ""
    );
    if (!next || next.trim() === "" || next.trim() === badge.title) return;
    try {
      await axios.patch(`${API}/badges/${badge.badge_id}`, { title: next.trim() }, { withCredentials: true });
      toast.success("Badge title updated.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update title.");
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader><CardTitle>Digital Badges</CardTitle></CardHeader>
        <CardContent className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</CardContent>
      </Card>
    );
  }

  const competency = data.badges.filter((b) => b.badge_type === "competency");
  const identity = data.badges.filter((b) => b.badge_type === "identity");
  const framework = data.badges.filter((b) => b.badge_type === "framework");

  return (
    <Card data-testid="badges-panel">
      <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-center gap-2">
          <Award className="w-5 h-5 text-amber-600" />
          Digital Badges
        </CardTitle>
        {data.auto_derived ? (
          <span className="text-[11px] text-slate-500" data-testid="badges-auto-source-hint">
            Auto-derived from locked assessor scores
          </span>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {!data.auto_derived ? (
          <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm" data-testid="badges-blocked-banner">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Badges not yet available.</p>
              <p className="text-xs mt-1">{data.trigger_message}</p>
            </div>
          </div>
        ) : null}

        {data.badges.length === 0 ? (
          data.auto_derived ? (
            <p className="text-sm text-slate-500" data-testid="badges-empty-derived">
              No competencies met the minimum score threshold yet. Badges will appear here automatically as the assessor finalises scores.
            </p>
          ) : null
        ) : (
          <>
            {[
              { key: "competency", arr: competency, label: "Competency Badges" },
              { key: "identity",   arr: identity,   label: "Professional Identity Badges" },
              { key: "framework",  arr: framework,  label: "Framework Achievement" },
            ].map(({ key, arr, label }) =>
              arr.length === 0 ? null : (
                <div key={key}>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{label}</p>
                  <div className="space-y-2">
                    {arr.map((b) => (
                      <BadgePill
                        key={b.badge_id}
                        badge={b}
                        onAction={actOnBadge}
                        onEditTitle={editBadgeTitle}
                        currentUserRole={currentUserRole}
                      />
                    ))}
                  </div>
                </div>
              )
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
